/* eslint-disable no-nested-ternary */
import Head from 'next/head';
import { SearchContext } from 'pages/recherche';
import React, { FC, useContext, useEffect } from 'react';
import { SecondaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { ContentCardLoader } from '@components/content/ContentCard/ContentCardLoader.component';
import { PlcMessageWithRetryProps } from '@components/PlcMessage/PlcMessageWithRetry.component';
import { Text } from '@components/Text/Text.component';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { buildContentCard } from '@utils/card';
import { usePlcRouter } from '@utils/customHooks';
import { defaultExtendedPagination } from '@utils/pagination';
import { formatUrlWithParam, urlEnum } from '@utils/url';
import dynamic from 'next/dynamic';

import './SearchResults.scss';

const PlcMessageWithRetry = dynamic<PlcMessageWithRetryProps>(
  () => import('@components/PlcMessage/PlcMessageWithRetry.component').then(mod => mod.PlcMessageWithRetry),
  {}
);

const SearchNoResult = dynamic<{}>(
  () => import('../SearchNoResult/SearchNoResult.component').then(mod => mod.SearchNoResult),
  {}
);

const SearchResults: FC = () => {
  const {
    results: { data: results, isValidating: loadingResults, error: errorResults, retryCallback: retryFetchResults },
    pagination: { data: tempPagination },
    nextResults: { fetchNextResults, isValidating: loadingNext },
  } = useContext(SearchContext);
  const pagination = tempPagination || defaultExtendedPagination;
  const isSmartphone = useIsBreakpointDown(Breakpoint.s);

  const cards = results?.map(item => buildContentCard(item, isSmartphone));
  const hasResult = cards && cards.length > 0;

  const onSeeMoreClick = () => {
    if (fetchNextResults) {
      fetchNextResults();
    }
  };

  const { query: routerQuery, push } = usePlcRouter();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [!fetchNextResults]);

  useEffect(() => {
    const { offset, count } = pagination;
    if (offset > count) {
      push('/404');
    }
  }, [pagination]);

  function buildSeoNextSearchPageLinks() {
    const { offset, limit, count } = pagination;
    const anchors: JSX.Element[] = [];
    for (let i = 0; i < count; i += limit) {
      const searchPage = Math.ceil(i / limit).toString();
      if (i === offset) {
        continue;
      }
      anchors.push(
        <PlcAnchor
          key={i}
          linkProps={{
            href: formatUrlWithParam(urlEnum.search, {
              ...routerQuery,
              page: searchPage,
            }),
          }}
        />
      );
    }
    return anchors;
  }

  const loading = loadingResults && !loadingNext;

  return (
    <main className="search-results wrapper-s">
      <Head>
        <link key="canonical" href={formatUrlWithParam(urlEnum.search, routerQuery)} rel="canonical" />
        {Number(routerQuery.page) > 0 && (
          <link
            key="link-prev"
            href={formatUrlWithParam(urlEnum.search, {
              ...routerQuery,
              page: (Number(routerQuery.page) - 1).toString(),
            })}
            rel="prev"
          />
        )}
        {pagination.hasNext && (
          <link
            key="link-next"
            href={formatUrlWithParam(urlEnum.search, {
              ...routerQuery,
              page: ((Number(routerQuery.page) || 0) + 1).toString(),
            })}
            rel="next"
          />
        )}
      </Head>
      <div className={hasResult || loading ? 'grid-m-3-s-2' : ''}>
        {loading ? (
          <>
            <ContentCardLoader />
            <ContentCardLoader />
            <ContentCardLoader />
          </>
        ) : errorResults ? (
          <PlcMessageWithRetry retryCallback={retryFetchResults} />
        ) : (
          hasResult && cards
        )}
        {loadingNext && (
          <>
            <ContentCardLoader />
            <ContentCardLoader />
            <ContentCardLoader />
          </>
        )}
        {!loading && !loadingNext && !hasResult && !errorResults && <SearchNoResult />}
      </div>
      {!loading && !loadingNext && hasResult && !errorResults && (
        <div className="search-pagination plc-mb-l plc-mb-up-m-xxl">
          <Text
            className="plc-mb-m"
            i18nKey="search.results.pagination"
            i18nOptions={{ current: pagination.current, total: pagination.count }}
            variant="p"
          />
          {pagination.hasNext && (
            <SecondaryButton onClick={onSeeMoreClick} textProps={{ i18nKey: 'search.results.see-more' }} />
          )}
          <div>{buildSeoNextSearchPageLinks()}</div>
        </div>
      )}
    </main>
  );
};

export { SearchResults };
